/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * The results list as compact rows — the 2026-10 redesign
 * (docs/plan-vergelijker-resultaten-2026-09-30.md). Started as a verbatim copy
 * of the old cards comparer (then src/components/ComparisonEngine.tsx) on
 * 2026-10-01 and reshaped from there; the two shipped side by side behind a
 * build-time flag until the cleanup of 2026-10-07 (plan §13.5) deleted the
 * cards and the flag, and this file took over their name.
 *
 * What it kept from the cards, on purpose: every filter and gate, the URL
 * contract (src/lib/comparerParams.ts), the one comparator
 * (src/lib/comparerSort.ts — the ranking disclosure above the list stays true
 * word for word), the two badges (whose rule, src/lib/relativeBullets.ts, since
 * 2026-10-08 also opens a row's Voordelen with what it is best at in the result
 * set, D36), the Dutch-only always-mounted details panel, the three modals and
 * every event name the funnel is measured on.
 *
 * What is new: one <ResultRow> per offer (src/components/comparison/ResultRow),
 * from xl a column-header line whose column words are the filters, with the
 * audience control and "wissen" at its start and the sort control at its end —
 * the one line of controls there, and the one that sticks (ResultHeaderRow;
 * the bar is below xl only since 2026-10-03), "Toon meer" in tens instead of
 * numbered pages (the bake still carries the first ten rows), a shortlist of
 * up to three offers that opens the existing /vergelijk page with the
 * visitor's own inputs (useShortlist + ShortlistTray), and above the list a
 * trust strip (order — the ranking disclosure —, revenue model, coverage, last
 * update; TrustStrip) with one basis line under it in place of the four
 * paragraphs the cards had.
 * For a visitor back on another day, what changed since their last visit sits
 * between the two (ChangesStrip, src/lib/compareChanges.ts) — or, when nothing
 * did, one clause in the trust strip's date. The filter and sort bar has three
 * layouts since 2026-10-08 (the filter-bar mockup; Jasper: "better alignment
 * and clearer intentionality of the sort & filter option"): from xl the
 * header line; from sm to xl one row of labelled filter pills ("Looptijd: 2
 * jaar") under a muted "Filters" label with the count, "wissen", and at its
 * right end a separate sort control ("Sorteren: Opbrengst ↓"); below sm two
 * buttons — "Alle filters" (n), a sheet that edits every filter as a draft and
 * writes the address once on apply (FilterSheet), and "Sorteren: …", a sheet
 * of the orders (SortSheet) — with one removable chip per filter in force and
 * "wissen" under them. The list and the sheet's live count share one filter rule
 * (src/lib/comparerFilter.ts). Every filter write replaces the history entry —
 * the pills, "wissen", the sheet and the chips alike: a filter state is not a
 * page the visitor went to, so Back leaves the comparer.
 */

import React, { useState, useMemo, useEffect, useCallback, useRef, startTransition } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { providerBackState } from '../../lib/providerBackLink';
import { type EREProvider } from '../../types';
import { useLanguage } from '../../contexts/LanguageContext';
import { usePrerender, type ProvidersMeta } from '../../contexts/PrerenderContext';
import { cachedFetch } from '../../lib/apiCache';
import { formatDecimal, formatInteger } from '../../lib/formatters';
import {
  SURFACES,
  providerEventParams,
  trackEvent,
  trackProviderSignupClick,
  trackProviderWebsiteClick,
  trackViewProfileClick,
} from '../../lib/gtag';
import {
  X,
  SlidersHorizontal,
  ArrowUpDown,
  ChevronDown,
  Users2,
  GitCompareArrows,
  Calculator,
} from 'lucide-react';
import { MotionConfig } from 'motion/react';
import { cn } from '../../lib/cn';
import { buildOutboundUrl } from '../../lib/outboundUrl';
import { KWH_TO_ERE_FACTOR, ereCountFor, estimateOfferNet } from '../../lib/earnings';
import { rowOfferPricing } from '../../lib/offerPricing.js';
import {
  ALL_DURATIONS,
  DEFAULT_DURATION_SELECTION,
  durationOptions,
  durationSelectionLabel,
  isDurationSelected,
  toggleDurationSelection,
} from '../../lib/durationOptions';
import {
  commissionSortValue,
  compareProviders,
  type SortDirection,
  type SortField,
} from '../../lib/comparerSort';
import { relativePluspunten, standingsIn } from '../../lib/relativeBullets';
import {
  buildShortlistHref,
  explicitComparerVolume,
  parseVolume,
  readComparerInputs,
} from '../../lib/comparerParams';
import {
  FILTER_DEFAULTS,
  activeComparerFilters,
  applyComparerFilters,
  availableFeatureCodes,
  effectiveComparerFilters,
  parseSmartChargingFilter,
  parseTermsFilter,
  type ComparerFilterGates,
  type ResettableFilter,
  type SmartChargingFilter,
} from '../../lib/comparerFilter';
import { FEATURE_LABEL_KEY } from '../../lib/providerFeatures';
import { useErePrice } from '../../hooks/useErePrice';
import { useShortlist } from '../../hooks/useShortlist';
import { useInitialViewState, useViewStateSync } from '../../hooks/useViewState';
import {
  diffSnapshots,
  dismissChangesForSession,
  isChangesDismissed,
  isEmptyDiff,
  isSameLocalDay,
  readSnapshot,
  snapshotOf,
  writeSnapshot,
  type CompareDiff,
  type CompareSnapshot,
} from '../../lib/compareChanges';
import { scrollToAnchor } from '../../lib/pageScroll';
import { getCommissionTier } from '../../lib/commissionTier';
import { fillTemplate } from '../../lib/templateVars.js';
import { SmartChargerInfoModal } from '../SmartChargerInfoModal';
import { LazyOnOpen } from '../LazyOnOpen';
import { MidCheckerModal, SignupModal, loadMidCheckerModal } from '../lazyModals';
import type { SignupContext } from '../signup/shared';
import { sanitizeLeadPublicItems } from '../../lib/leadItems.js';
import { ErrorBoundary } from '../ErrorBoundary';
import { ROW_ENTRANCE_MS, ResultRow } from './ResultRow';
import { ResultHeaderRow, type HeaderFilter } from './ResultHeaderRow';
import { FilterSelect, type FilterOption } from './FilterSelect';
import { ShortlistTray } from './ShortlistTray';
import { TrustStrip } from './TrustStrip';
import { ChangesStrip, changedOfferCount, changeLines } from './ChangesStrip';
import { FilterSheet, type FilterSheetDraft } from './FilterSheet';
import { SortSheet } from './SortSheet';

// No Google-score filter, sort or review line: the ratings path was removed on
// 2026-10-14 (no record carried a score). An old address with `?minRating=`
// simply shows the unfiltered list, and a remembered "hoogste score" sort
// falls back to the default (parseComparerView).

export interface ComparisonEngineProps {
  providers: EREProvider[];
}

/**
 * The basis line's id: the note the asterisk of "Netto per jaar*" (the header
 * line, xl) points at, which starts with that asterisk. One engine per page.
 */
const BASIS_LINE_ID = 'comparer-basis-line';

/**
 * Everything under the bar — the applied-filter chips, the rows (or the empty
 * state), "Toon meer" — in one block: a sheet apply scrolls its top under the
 * bar (src/lib/pageScroll.ts).
 */
const RESULTS_ID = 'compare-results';

/** What changed since the visitor's previous visit on an earlier day (plan §12.4 B3). */
interface VisitChanges {
  /** The previous visit (epoch ms): the strip's date and the trust strip's quiet clause. */
  since: number;
  diff: CompareDiff;
}

/** Each resettable filter's URL parameter (the key) and its `filter_change` name (the value). */
const FILTER_EVENT_NAMES: Record<ResettableFilter, string> = {
  contractDuration: 'contract_duration',
  smartCharging: 'smart_charging',
  terms: 'terms',
};

/** Rows per batch: the bake carries the first batch, "Toon meer" reveals the rest. */
const ITEMS_PER_PAGE = 10;

/** The sort a first visit gets (`${field}_${direction}`, the sort pills' value). */
const DEFAULT_SORT_KEY = 'earnings_desc';
/**
 * Every sort a pill can offer: what a remembered sort may be. The retired
 * `rating_desc` is not among them, so a list remembered under it comes back
 * in the default order.
 */
const RESTORABLE_SORT_KEYS: ReadonlySet<string> = new Set([
  'commission_asc',
  'commission_desc',
  'contractDuration_asc',
  'earnings_desc',
]);
/** More open rows than this is not something a visitor did; the rest is dropped. */
const MAX_RESTORED_EXPANDED = 100;

/**
 * The phone bar's two buttons (below sm): 36 px tall, the bar's whole height
 * a tap target through a transparent ::before. Light: a white button —
 * literal hexes, because the slate tokens make a pale field on the pale bar
 * and --color-white is ink there.
 */
const PHONE_BAR_BUTTON_CLASS =
  'relative inline-flex h-9 min-w-0 flex-auto items-center justify-center gap-1 rounded-[10px] border bg-slate-800 px-2.5 text-xs font-bold text-slate-200 transition-colors hover:bg-slate-700/70 focus-ring before:absolute before:inset-x-0 before:-inset-y-1.5 [html.light_&]:bg-[#ffffff] [html.light_&]:hover:bg-[#f1f5f9]';
/** Its border: emerald while something it opens is away from the default, else slate-400 in light. */
const phoneSetClass = (set: boolean) =>
  set
    ? 'border-emerald-500/55 inset-ring inset-ring-emerald-500/25'
    : 'border-slate-600/85 [html.light_&]:border-[#94a3b8]';
/** The count of filters in force, on "Filters" (sm to xl) and "Alle filters" (phones). */
const COUNT_BADGE_CLASS =
  'inline-grid h-4 min-w-4 place-items-center rounded-full bg-emerald-700 px-1 text-[10px] font-black leading-none tracking-normal tabular-nums text-white';

/**
 * The providers file's write time as this document last received it (the
 * trust strip's "Aanbod bijgewerkt op"). Module state, written only when the
 * fetch answers after mount — so empty on every hydrating render, like the
 * view-state map — and read by the next mount in this document.
 */
let knownProvidersUpdatedAt: string | null = null;

/** Tests only: a fresh document, which has received no date yet. */
export const resetKnownProvidersDateForTests = (): void => {
  knownProvidersUpdatedAt = null;
};

/**
 * The list as the visitor left it in a history entry (src/lib/viewState.ts,
 * slot `comparer`): which rows were open, how far "Toon meer" had gone, the
 * sort, and whether the trust strip's phone fold was open (it moves every row
 * under it). Filters are in the address already, the shortlist in localStorage.
 */
interface ComparerView {
  expanded: string[];
  visibleCount: number;
  sort: string;
  trustOpen: boolean;
}

/** A sort key's two halves, split at the LAST underscore (`contractDuration_asc`). */
const splitSortKey = (key: string): { field: SortField; direction: SortDirection } => {
  const at = key.lastIndexOf('_');
  return { field: key.slice(0, at) as SortField, direction: key.slice(at + 1) as SortDirection };
};

/**
 * What storage gave back, made safe: an unknown sort falls back to the default
 * while the rest still restores, the count is at least one batch, and only
 * strings count as row ids. Ids are NOT checked against the rows — they may
 * not have loaded yet, and an id no row has opens nothing.
 */
const parseComparerView = (raw: unknown): ComparerView | null => {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const r = raw as Partial<Record<keyof ComparerView, unknown>>;
  const expanded = Array.isArray(r.expanded)
    ? [...new Set(r.expanded.filter((id): id is string => typeof id === 'string'))].slice(
        0,
        MAX_RESTORED_EXPANDED
      )
    : [];
  const count =
    typeof r.visibleCount === 'number' && Number.isFinite(r.visibleCount)
      ? Math.floor(r.visibleCount)
      : ITEMS_PER_PAGE;
  const sort =
    typeof r.sort === 'string' && RESTORABLE_SORT_KEYS.has(r.sort) ? r.sort : DEFAULT_SORT_KEY;
  return {
    expanded,
    visibleCount: Math.max(ITEMS_PER_PAGE, count),
    sort,
    trustOpen: r.trustOpen === true,
  };
};

const isDefaultComparerView = (view: ComparerView): boolean =>
  view.expanded.length === 0 &&
  view.visibleCount === ITEMS_PER_PAGE &&
  view.sort === DEFAULT_SORT_KEY &&
  !view.trustOpen;

/**
 * Renders its children once `target` has come into view (a quarter of a
 * viewport early) or sits above the viewport, and from then on. The flag lives
 * here rather than in the engine: the results block starts just under the hero,
 * so it counts as in view on load, and flipping a flag in the engine re-rendered
 * every row right after hydration (2026-10-02 phone profile).
 */
const AfterInView: React.FC<{
  target: React.RefObject<HTMLElement | null>;
  children: React.ReactNode;
}> = ({ target, children }) => {
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    if (seen) return;
    const el = target.current;
    if (!el) return;
    if (typeof IntersectionObserver === 'undefined') {
      setSeen(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting || e.boundingClientRect.top <= 0)) {
          observer.disconnect();
          setSeen(true);
        }
      },
      { rootMargin: '0px 0px 25% 0px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [seen, target]);
  return seen ? <>{children}</> : null;
};

export const ComparisonEngine: React.FC<ComparisonEngineProps> = ({ providers }) => {
  const { t, language } = useLanguage();
  // CMS-managed €/ERE — the leading price figure for every payout estimate.
  const erePrice = useErePrice();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  // The list as the visitor left this history entry, when this document has
  // it in memory (an in-tab Back/Forward): open rows, "Toon meer", the sort
  // come back at the FIRST render, so the scroll manager restores the position
  // onto the final layout. Null on every hydrating render — the map is empty
  // until this document writes it — so the bake and its hydration agree. A
  // reload restores after mount instead (useViewStateSync below).
  const restoredView = useInitialViewState('comparer', parseComparerView);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(
    () => new Set(restoredView?.expanded ?? [])
  );
  // "Toon meer": how many rows are on screen. The bake renders the first batch;
  // every reveal adds one more. Never URL state (it would push those URLs onto
  // the SPA-shell path) and reset with the filters, like the page used to be.
  const [visibleCount, setVisibleCount] = useState(
    () => restoredView?.visibleCount ?? ITEMS_PER_PAGE
  );
  // The first row of the batch just revealed, to hand focus to — null between reveals.
  const [revealFocusIndex, setRevealFocusIndex] = useState<number | null>(null);
  // The rows' entrance (.reveal-rise, ResultRow) plays on the first paint and on
  // the batch a "Toon meer" click reveals — never when a sort or a filter moves
  // rows that are already on screen. React MOVES a re-sorted <li>, and a moved
  // element restarts its CSS animation: with fill-mode backwards a row sat
  // invisible for its stagger delay after every header click (8 of 10 rows,
  // ~0.4 s, measured 2026-10-01). `entranceDone` is false at render on both
  // sides of the bake (every row carries the class, as baked) and turns true
  // once the longest first-paint entrance has played; only then does a row
  // drop the class, so the hydrating render never differs from the HTML.
  // A restored list is no first paint: it is where the visitor left it, at its
  // final height, with nothing rising into place (and a client mount, never a
  // hydration — see `restoredView`).
  const [entranceDone, setEntranceDone] = useState(() => restoredView !== null);
  // The batch the latest "Toon meer" revealed: its first index, in the result set it was
  // revealed in (`resultSetKey` below). Another result set — a sort, a filter — ends it.
  const [revealedBatch, setRevealedBatch] = useState<{ from: number; resultSet: string } | null>(
    null
  );
  const [sortField, setSortField] = useState<SortField>(
    () => splitSortKey(restoredView?.sort ?? DEFAULT_SORT_KEY).field
  );
  const [sortDirection, setSortDirection] = useState<SortDirection>(
    () => splitSortKey(restoredView?.sort ?? DEFAULT_SORT_KEY).direction
  );
  // The trust strip's phone fold (TrustStrip): whether its items 2–4 show
  // below sm. Closed in the bake and its hydration, like every row.
  const [trustOpen, setTrustOpen] = useState(() => restoredView?.trustOpen ?? false);
  const toggleTrustOpen = () => setTrustOpen((open) => !open);
  // Bumped when open rows are restored after mount (a reload): every details
  // panel remounts in the state it is in, so an open one renders open instead
  // of animating open under the scroll manager's restore (ResultRow).
  const [detailsEpoch, setDetailsEpoch] = useState(0);
  const [smartChargingInfoOpen, setSmartChargingInfoOpen] = useState(false);
  const [midCheckerOpen, setMidCheckerOpen] = useState(false);
  // The mid-checker's only trigger here is the button INSIDE the smart-charging
  // info modal (an eager native dialog whose own file stays as it is), so there
  // is no trigger of ours to hang a hover preload on: the chunk starts loading
  // when that dialog opens, one click before it is asked for.
  useEffect(() => {
    if (smartChargingInfoOpen) loadMidCheckerModal().catch(() => {});
  }, [smartChargingInfoOpen]);
  // One signup modal for the whole engine, context-driven — never mounted
  // inside the row map.
  const [signupCtx, setSignupCtx] = useState<SignupContext | null>(null);
  // "Alle filters" and "Sorteren" (phones): the two sheets and the buttons
  // that open them, which take the focus back when they close.
  const [sheetOpen, setSheetOpen] = useState(false);
  const sheetTriggerRef = useRef<HTMLButtonElement>(null);
  const [sortSheetOpen, setSortSheetOpen] = useState(false);
  const sortSheetTriggerRef = useRef<HTMLButtonElement>(null);

  // ─── The trust strip's facts ───
  // "Aanbod bijgewerkt op …": the providers file's write time. The bake seeds it
  // beside the providers (`providersMeta`), so the baked strip and the hydrating
  // render print the same day. A shell-served (filtered) URL has no seed: the
  // item appears once the fetch below has answered. The fetch runs on every
  // mount, seeded or not, because MainApp (App.tsx) refetches the providers
  // after hydration too, and the date belongs to the rows on screen — a later
  // value only ever arrives after hydration, as a state update. Never in render.
  //
  // A REMOUNT in this document (Back to the comparer) starts from the date
  // this document last received (`knownProvidersUpdatedAt`): the item would
  // otherwise arrive after the first render and push the rows down — on a
  // phone the strip is one column, so by a line, under the position the
  // scroll manager has just restored (measured: 24 px at 390 px). Empty until
  // this document's own fetch answered, so never on the hydrating render.
  const seededUpdatedAt = usePrerender()?.providersMeta?.updatedAt ?? null;
  const [providersUpdatedAt, setProvidersUpdatedAt] = useState<string | null>(
    () => seededUpdatedAt ?? knownProvidersUpdatedAt
  );
  // Whether the date the strip will show is in: the scroll manager waits for
  // it after a reload, as it waits for the restored rows (useViewStateSync).
  const [providersMetaSettled, setProvidersMetaSettled] = useState(
    () => providersUpdatedAt !== null
  );
  useEffect(() => {
    let cancelled = false;
    cachedFetch<ProvidersMeta>('/api/providers/meta')
      .then((meta) => {
        const next = meta?.updatedAt;
        if (typeof next === 'string' && next) knownProvidersUpdatedAt = next;
        if (!cancelled && typeof next === 'string' && next) setProvidersUpdatedAt(next);
      })
      .catch(() => {
        // Keep what the bake seeded; without a seed the item simply stays away.
      })
      .finally(() => {
        if (!cancelled) setProvidersMetaSettled(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);
  // Coverage, over the whole data set rather than the filtered view (the <h2>
  // counts the view): every offer, and the providers behind them by slug.
  const coverage = useMemo(
    () => ({ offers: providers.length, providers: new Set(providers.map((p) => p.slug)).size }),
    [providers]
  );

  // The URL contract, read through the one module /vergelijk reads it with
  // (src/lib/comparerParams.ts): a whitelisted audience (anything else is the
  // default, so a typo in a shared link cannot render an empty comparer), the
  // volume with the audience's own default, the looptijd with every one of
  // them ('Alle', since 2026-10-16; a multi-select, src/lib/durationOptions.ts).
  const { audience, volume, contractDuration } = readComparerInputs(searchParams);
  // The other two filters, read by the one filter module (src/lib/comparerFilter.ts):
  // the smart-charger value with its older provider-perspective spellings
  // migrated, the Voorwaarden value whitelisted like `audience` (a value off
  // FEATURE_CODES filters nothing instead of emptying the list).
  const smartChargingFilter = parseSmartChargingFilter(searchParams.get('smartCharging'));
  const termsFilter = parseTermsFilter(searchParams.get('terms'));

  // A control that cannot change the answer is not a control: a Voorwaarden
  // option exists only while at least one provider in the WHOLE set earns it
  // (src/lib/providerFeatures.ts derives them from reviewed dossier facts,
  // never from a ticked box), so a filter can never be a dead end. Measured
  // over the WHOLE provider set, not the filtered view: gating on the view
  // would let a filter that empties the results delete the very control
  // needed to undo it.
  const availableFeatures = useMemo(() => availableFeatureCodes(providers), [providers]);
  const filterGates = useMemo<ComparerFilterGates>(
    () => ({ features: availableFeatures }),
    [availableFeatures]
  );

  // ...and a filter that has no control must not silently filter. An older
  // shared link (or a bookmark from before the gate) can still carry a
  // Voorwaarden value nobody earns today; applying it while its option is
  // hidden would empty the list with nothing on screen to undo it. Ignored,
  // not rewritten: the param stays in the URL and starts working again the day
  // a provider earns it.
  const effectiveFilters = effectiveComparerFilters(
    {
      audience,
      contractDuration,
      smartCharging: smartChargingFilter,
      terms: termsFilter,
    },
    filterGates
  );
  const effectiveTerms = effectiveFilters.terms;

  // Functional form (like Filters' setParam): a snapshot of `searchParams`
  // from this render would silently drop params written by another control
  // (hero filters vs results bar) between renders. Replaced, not pushed — the
  // one history rule of every filter write (the Stap 1 card, the sheet, the
  // chips, "wissen"): a filter state is not a page the visitor went to, and a
  // pushed entry per pill made Back step through every pill instead of
  // leaving the comparer.
  const setSearchParam = useCallback(
    (key: string, val: string) => {
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          p.set(key, val);
          return p;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const trackFilterChange = useCallback(
    (filter: string, value: string | number) => {
      trackEvent('filter_change', {
        filter_name: filter,
        filter_value: String(value),
        surface: SURFACES.resultsBar,
        language,
      });
    },
    [language]
  );

  const setAudience = useCallback(
    (val: string) => {
      setSearchParam('audience', val);
      trackFilterChange('audience', val);
    },
    [setSearchParam, trackFilterChange]
  );

  // Derived from the rows, never hardcoded — see src/lib/durationOptions.ts.
  // A duration nobody offers is a guaranteed empty result; a duration somebody
  // starts offering appears on its own.
  const contractOptions = useMemo(
    () => durationOptions(providers, contractDuration, t),
    [providers, contractDuration, t]
  );
  const offeredDurations = useMemo(
    () => contractOptions.filter((o) => o.value !== ALL_DURATIONS).map((o) => o.value),
    [contractOptions]
  );

  // A pick is a toggle of one looptijd (the multi-select): the rule module
  // turns it into the next value, and a pick that changes nothing (unticking
  // the last one) writes nothing and reports nothing.
  const setContractDuration = useCallback(
    (option: string) => {
      const next = toggleDurationSelection(contractDuration, option, offeredDurations);
      if (next === contractDuration) return;
      setSearchParam('contractDuration', next);
      trackFilterChange('contract_duration', next);
    },
    [contractDuration, offeredDurations, setSearchParam, trackFilterChange]
  );

  const setSmartChargingFilter = useCallback(
    (val: SmartChargingFilter) => {
      setSearchParam('smartCharging', val);
      trackFilterChange('smart_charging', val);
    },
    [setSearchParam, trackFilterChange]
  );

  const setTermsFilter = useCallback(
    (val: string) => {
      setSearchParam('terms', val);
      trackFilterChange('terms', val);
    },
    [setSearchParam, trackFilterChange]
  );

  // BTW convention: Particulier contracts have commission + service costs stored INCL 21% BTW
  // (non-deductible for consumers), Zakelijk contracts are stored EX BTW (deductible for businesses).
  // Both commissionFee and serviceCosts are used as-is — no additional VAT multiplier.
  // Returns null when the volume is 0 or either input is unknown, so a row with
  // missing data never projects a number it can't stand behind. The offer's own
  // pricing model (rowOfferPricing: a staffel's band, a vastrecht per kWh with
  // its yearly bounds) at the visitor's volume — one cost function for the
  // figure, the sort and the badges; a flat row is its commissionFee, as before.
  const calculateEarnings = useCallback(
    (p: EREProvider) => estimateOfferNet(volume, rowOfferPricing(p), p.serviceCosts, erePrice),
    [volume, erePrice]
  );
  // The % the tier word, the sort and the crown compare: a flat row's
  // commissionFee, a volume-dependent offer's effective % at the visitor's volume.
  const commissionAt = useCallback(
    (p: EREProvider) => commissionSortValue(p, volume, erePrice),
    [volume, erePrice]
  );

  // Encode sort as a single key, e.g. "commission_asc" — the ONE sort state,
  // shared by the bar's sort pill (below xl) and the header line's (from xl).
  const sortKey = `${sortField}_${sortDirection}`;

  const handleSortSelect = useCallback(
    (key: string) => {
      // The sort already in force — a pill re-picking its own option: nothing
      // changes, so nothing is reported.
      if (key === sortKey) return;
      const lastUnderscore = key.lastIndexOf('_');
      const field = key.slice(0, lastUnderscore) as SortField;
      const dir = key.slice(lastUnderscore + 1) as SortDirection;
      // One control in two places — the bar's pill below xl, the header line's
      // from xl, never both on screen — so one surface. The column headers that
      // sorted as `comparison_table` are plain words since 2026-10-01.
      trackEvent('sort_change', {
        sort_field: field,
        sort_direction: dir,
        surface: SURFACES.resultsBar,
        language,
      });
      setSortField(field);
      setSortDirection(dir);
    },
    [language, sortKey]
  );

  // The filters "wissen" can actually reset that are away from their default:
  // one applied-filter chip each on a phone, and their number is the "(n)" on
  // the filter button and on "wissen". Audience is deliberately NOT among them:
  // it is the visitor's own segment, and clearAllFilters leaves it alone —
  // counting it would strand the badge at 1 right after a clear. Over the
  // *effective* filters: an ignored Voorwaarden value narrows nothing, so
  // counting it would show "1 filter actief" over an unfiltered list.
  const activeFilters = activeComparerFilters(effectiveFilters);
  const activeFilterCount = activeFilters.length;

  const clearAllFilters = useCallback(() => {
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        // `audience` is left exactly as the visitor set it. This used to write
        // 'Particulier', so a zakelijke bezoeker who pressed the empty state's
        // only button was silently moved into consumer results.
        p.set('contractDuration', DEFAULT_DURATION_SELECTION);
        p.set('smartCharging', 'all');
        p.set('terms', 'all');
        return p;
      },
      // Replaced, like every filter write (setSearchParam above).
      { replace: true }
    );
    setSortField('earnings');
    setSortDirection('desc');
    trackEvent('filter_clear', {
      active_filter_count: activeFilterCount,
      surface: SURFACES.resultsBar,
      language,
    });
  }, [setSearchParams, activeFilterCount, language]);

  // ─── Option lists ───
  const audienceOptions = useMemo(
    () => [
      { label: t('filters.all'), value: 'Alle' },
      { label: t('filters.particulier'), value: 'Particulier' },
      { label: t('filters.zakelijk'), value: 'Zakelijk' },
    ],
    [t]
  );

  const smartOptions = useMemo(
    () => [
      { label: t('table.filter_smart_all'), value: 'all' },
      { label: t('table.filter_smart_has_opt'), value: 'has' },
      { label: t('table.filter_smart_none_opt'), value: 'none' },
    ],
    [t]
  );

  // The codes are the URL's data identifiers and never change; the words of
  // each come from the key FEATURE_LABEL_KEY names (src/lib/providerFeatures.ts)
  // — `short_notice` reads `table.filter_terms_late_deadline` since 2026-10-07.
  // The bar's pill, the header word and the chip all take this one list; the
  // filter sheet builds its twin from the same map.
  const termsOptions = useMemo(
    () => [
      { label: t('table.filter_terms_all'), value: 'all' },
      ...availableFeatures.map((code) => ({
        label: t(FEATURE_LABEL_KEY[code]),
        value: code,
      })),
    ],
    [t, availableFeatures]
  );

  // Each option carries a phone-width form: the column's own name (the
  // `table.sort_<column>` keys both baselines already have) plus the order as
  // an arrow — "Opbrengst ↓" for highest first, "Looptijd ↑" for shortest
  // first — so the pinned sort chip stays about half the width of its full
  // label and the filters next to it keep their room.
  const sortOptions = useMemo(() => {
    const opts: FilterOption[] = [
      {
        label: t('table.sort_commission_low'),
        shortLabel: `${t('table.sort_commission')} ↑`,
        value: 'commission_asc',
      },
      {
        label: t('table.sort_commission_high'),
        shortLabel: `${t('table.sort_commission')} ↓`,
        value: 'commission_desc',
      },
      {
        label: t('table.sort_duration_short'),
        shortLabel: `${t('table.sort_contract_duration')} ↑`,
        value: 'contractDuration_asc',
      },
    ];
    if (volume > 0)
      opts.push({
        label: t('table.sort_earnings_high'),
        shortLabel: `${t('table.sort_earnings')} ↓`,
        value: 'earnings_desc',
      });
    return opts;
  }, [t, volume]);

  // ─── The controls, described once ───
  // Every filter as a control — value, options, setter, default — rendered
  // twice: as the bar's pills below xl and as the header line's audience
  // control and column words from xl (2026-10-03). Two views of one address:
  // the same setters, so the same URL writes and the same `filter_change`
  // events whichever is pressed (and the phone's sheet writes the same
  // parameters). A filter whose control is gated off is null in both.
  const audienceControl: HeaderFilter = {
    value: audience,
    options: audienceOptions,
    onChange: setAudience,
    defaultValue: 'Particulier',
  };
  const durationControl: HeaderFilter = {
    value: contractDuration,
    options: contractOptions,
    onChange: setContractDuration,
    defaultValue: DEFAULT_DURATION_SELECTION,
    multiple: true,
    isSelected: (option) => isDurationSelected(contractDuration, option),
    valueText: durationSelectionLabel(
      contractDuration,
      contractOptions,
      t('filters.all_durations'),
      (count) =>
        fillTemplate(t('filters.durations_count'), { count: formatInteger(count, language) })
    ),
  };
  const smartControl: HeaderFilter = {
    value: smartChargingFilter,
    options: smartOptions,
    onChange: (v) => setSmartChargingFilter(v as SmartChargingFilter),
    defaultValue: 'all',
  };
  const termsControl: HeaderFilter | null =
    availableFeatures.length > 0
      ? {
          value: effectiveTerms,
          options: termsOptions,
          onChange: setTermsFilter,
          defaultValue: 'all',
        }
      : null;
  // ─── What the controls say ───
  // "<name>:" before a value (`table.filter_pill_label`): the bar's pills, the
  // header line's audience and every sort control. A filter on the option
  // that filters nothing says "alle" (`table.filter_pill_all`) instead of
  // repeating its own name ("Voorwaarden: Alle voorwaarden").
  const pillLabel = (name: string) => fillTemplate(t('table.filter_pill_label'), { name });
  const allWord = t('table.filter_pill_all');
  const sortName = t('table.sort_label');
  const sortLabel = pillLabel(sortName);
  const sortSelected = sortOptions.find((o) => o.value === sortKey);
  // The phone's button to the filter sheet is named "Alle filters (n)", the
  // bar's "Filters" label (sm to xl, a button to the same sheet below lg)
  // "Filters (n)": each by what it shows, with the count.
  const countSuffix =
    activeFilterCount > 0 ? ` (${formatInteger(activeFilterCount, language)})` : '';
  const filterSheetName = `${t('filters.sheet_open')}${countSuffix}`;
  const filtersName = `${t('table.filters_label')}${countSuffix}`;
  const filtersLabelContent = (
    <>
      <SlidersHorizontal className="w-3 h-3 shrink-0" aria-hidden="true" />
      {t('table.filters_label')}
      {activeFilterCount > 0 && (
        <span className={COUNT_BADGE_CLASS}>{formatInteger(activeFilterCount, language)}</span>
      )}
    </>
  );

  // The sort control: ONE state (`sortKey`) and one handler, drawn once per
  // layout and only one of them on screen — in the header line from xl
  // ("Sorteren: Hoogste opbrengst", the line's own grammar), boxed at the
  // bar's right end from sm to xl ("Sorteren: Opbrengst ↓", the short order
  // beside four pills), and below sm the button that opens the sort sheet.
  // Each says "Sorteren" in sight (it existed as an aria-label only until
  // 2026-10-08) and is named "Sorteren: <order>".
  const headerSortControl = (
    <FilterSelect
      variant="column"
      edge="none"
      name={sortName}
      label={sortLabel}
      value={sortKey}
      options={sortOptions}
      onChange={handleSortSelect}
      defaultValue={DEFAULT_SORT_KEY}
    />
  );
  const barSortControl = (
    <FilterSelect
      variant="boxed"
      icon={ArrowUpDown}
      short
      name={sortName}
      label={sortLabel}
      value={sortKey}
      options={sortOptions}
      onChange={handleSortSelect}
      defaultValue={DEFAULT_SORT_KEY}
    />
  );

  // ─── Filter + sort ───
  const filteredProviders = useMemo(
    () =>
      // One filter rule for the list and for the filter sheet's live count
      // (src/lib/comparerFilter.ts), so the two can never disagree. It narrows,
      // never re-orders: the ranking disclosure (the trust strip) stays true.
      applyComparerFilters(providers, {
        audience,
        contractDuration,
        smartCharging: smartChargingFilter,
        terms: effectiveTerms,
      })
        // One comparator for both renderers of the list (src/lib/comparerSort.ts):
        // the sorted column, then the tie closer under the payout sort, then the
        // lowest commission, then the closer, then the code-point name. An
        // offer whose cost follows the volume compares on its effective % at
        // the visitor's volume; a flat row on its commissionFee.
        .sort((a, b) =>
          compareProviders(a, b, {
            sortField,
            sortDirection,
            earningsOf: calculateEarnings,
            commissionOf: commissionAt,
          })
        ),
    [
      providers,
      audience,
      contractDuration,
      smartChargingFilter,
      effectiveTerms,
      sortField,
      sortDirection,
      calculateEarnings,
      commissionAt,
    ]
  );

  // Where each row stands in the result set — every row the filters keep, not
  // just the ones on screen, so a claim is true of the whole list: the lowest
  // commission among rows with complete data (subscriptions left out: their 0%
  // is no commission-model price), the highest estimated net payout (every
  // pricing model: the estimate nets out the commission and the service costs).
  // ONE rule, src/lib/relativeBullets.ts, behind the two
  // badges ("Laagste commissie", "Hoogste opbrengst") and the relative
  // pluspunten that lead a row's Voordelen (D36): a badge and a point never
  // name different rows.
  const standingOf = useMemo(
    () =>
      standingsIn(filteredProviders, {
        earningsOf: calculateEarnings,
        commissionOf: commissionAt,
      }),
    [filteredProviders, calculateEarnings, commissionAt]
  );

  // The inputs that make a result set: the filters as they act, the volume
  // (it ranks the payout sort) and the sort. A revealed batch belongs to the
  // set it was revealed in.
  const resultSetKeyFor = (field: SortField, direction: SortDirection) =>
    [
      audience,
      volume,
      contractDuration,
      smartChargingFilter,
      effectiveTerms,
      field,
      direction,
    ].join('|');
  const resultSetKey = resultSetKeyFor(sortField, sortDirection);

  // A new result set starts at the first batch with nothing expanded and no
  // batch animating in — only when the set really CHANGED since the render
  // before: never on mount (a list restored for this history entry must keep
  // its open rows and its "Toon meer"), never twice under StrictMode, and never
  // for the sort a restore sets (it moves `ackResultSet` along in the same
  // batch). Reset while rendering, React's pattern for state that follows a
  // changed input: the new set never commits with the old set's count, and the
  // view-state save after the commit sees the reset values. An already-empty
  // set is kept: a fresh `new Set()` is a new state and re-rendered every row
  // right after hydration (2026-10-02 phone profile).
  const [ackResultSet, setAckResultSet] = useState(resultSetKey);
  if (ackResultSet !== resultSetKey) {
    setAckResultSet(resultSetKey);
    setVisibleCount(ITEMS_PER_PAGE);
    setExpandedIds((prev) => (prev.size === 0 ? prev : new Set()));
    setRevealedBatch(null);
  }

  const visibleProviders = useMemo(
    () => filteredProviders.slice(0, visibleCount),
    [filteredProviders, visibleCount]
  );
  const remaining = filteredProviders.length - visibleProviders.length;

  const revealedFrom = revealedBatch?.resultSet === resultSetKey ? revealedBatch.from : null;

  // ─── The list per history entry (src/lib/viewState.ts) ───
  // Saved under the entry the visitor is on whenever it changes — a filter
  // write replaces the entry, so its new key gets the reset list. After a full
  // document load onto an entry that had state (a reload, Back/Forward across
  // documents), the store hands it back here, after mount: sort, count, open
  // rows and the trust strip's fold in ONE batch, with the result set
  // acknowledged so the sort does not reset the count and the open rows, no
  // entrance and the panels remounted open. A
  // restore reports nothing — no `sort_change`, no expand, no "Toon meer";
  // only `results_view`, once, with the restored sort (the flag below).
  const currentView: ComparerView = {
    expanded: [...expandedIds],
    visibleCount,
    sort: sortKey,
    trustOpen,
  };
  /** Set by a restore that changes the sort: the mount's results_view waits for it. */
  const skipResultsViewRef = useRef(false);
  useViewStateSync<ComparerView>({
    slot: 'comparer',
    value: currentView,
    isDefault: isDefaultComparerView(currentView),
    // The trust strip's date grows the block above the rows: wait for it too.
    ready: providersMetaSettled,
    parse: parseComparerView,
    onLateRestore: (view) => {
      const { field, direction } = splitSortKey(view.sort);
      if (view.sort !== sortKey) skipResultsViewRef.current = true;
      setSortField(field);
      setSortDirection(direction);
      setAckResultSet(resultSetKeyFor(field, direction));
      setVisibleCount(view.visibleCount);
      setExpandedIds(new Set(view.expanded));
      setTrustOpen(view.trustOpen);
      setRevealedBatch(null);
      setEntranceDone(true);
      setDetailsEpoch((n) => n + 1);
    },
  });

  // The first-paint entrance ends when its longest stagger has played. It
  // re-renders every row, and nothing on screen changes with it, so it is a
  // transition: time-sliced, never one long task during the page's load.
  useEffect(() => {
    const timer = window.setTimeout(
      () => startTransition(() => setEntranceDone(true)),
      ROW_ENTRANCE_MS
    );
    return () => window.clearTimeout(timer);
  }, []);

  /**
   * A row's place in its entrance stagger, or null for none. A revealed batch
   * staggers from its own first row, so the row that takes the focus is visible
   * at once; the first paint staggers by rank, as baked.
   */
  const entranceStepOf = (index: number): number | null => {
    if (revealedFrom !== null && index >= revealedFrom) return index - revealedFrom;
    return entranceDone ? null : index;
  };

  // Keep each row's pros/cons panel in the DOM while it is collapsed, so the
  // baked HTML carries the verdict a crawler can read. Dutch only — see the
  // row itself for why English still mounts on expand. A pure function of
  // the URL language, so the server render and the hydrated tree agree.
  const mountDetails = language === 'nl';

  // ─── Aggregate "results viewed" signal ───
  // One low-volume event per distinct result set the visitor lands on
  // (initial load, each filter/sort change). Pairs with the per-click provider
  // events to form a CTR funnel without per-row spam. `slugs` = the first
  // batch in rank order — the impression list behind per-provider CTR at a
  // position and the evidence behind "ranking is not for sale"; later batches
  // are `results_show_more`, each with its own slugs.
  const lastResultsSig = useRef('');
  useEffect(() => {
    // A restore after mount is about to change the sort: the result set this
    // effect sees on mount is not the one the visitor will be shown.
    if (skipResultsViewRef.current) {
      skipResultsViewRef.current = false;
      return;
    }
    const firstBatch = filteredProviders.slice(0, ITEMS_PER_PAGE);
    const slugs = firstBatch.map((p) => p.slug).join(',');
    const sig = [
      filteredProviders.length,
      audience,
      contractDuration,
      sortField,
      sortDirection,
      effectiveTerms,
      activeFilterCount,
      slugs,
    ].join('|');
    if (sig === lastResultsSig.current) return;
    lastResultsSig.current = sig;
    trackEvent('results_view', {
      provider_count: filteredProviders.length,
      audience,
      contract_duration: contractDuration,
      sort_field: sortField,
      sort_direction: sortDirection,
      active_filter_count: activeFilterCount,
      slugs,
      surface: SURFACES.comparisonTable,
      language,
    });
  }, [
    filteredProviders,
    audience,
    contractDuration,
    sortField,
    sortDirection,
    effectiveTerms,
    activeFilterCount,
    language,
  ]);

  // ─── "Sinds je bezoek" (plan §12.4 B3) ───
  // What this browser was shown last time against what the comparison holds
  // now. Storage is the browser's, so the state is empty on both sides of the
  // bake and ONE effect fills it, once, as soon as the rows are there: a
  // snapshot of nothing would later read as "everything is new". The order is
  // the promise — read the previous snapshot, take the diff over ALL the rows
  // (a filtered view would make every offer outside it "gone"), hold both, and
  // only then write today's snapshot; never in render. StrictMode runs a mount
  // effect twice, and the second run must not read back the snapshot the first
  // one wrote — it is today's, so the strip would never show: the ref makes
  // both runs take the same diff and write the same snapshot. A visit earlier
  // the same day (the site's day, Amsterdam) gets nothing at all; a diff with
  // nothing in it gets no strip, only the trust strip's quiet clause.
  const [visitChanges, setVisitChanges] = useState<VisitChanges | null>(null);
  const [changesDismissed, setChangesDismissed] = useState(false);
  const changesTakenRef = useRef<{
    changes: VisitChanges | null;
    snapshot: CompareSnapshot;
  } | null>(null);
  const hasProviders = providers.length > 0;
  useEffect(() => {
    if (!hasProviders) return;
    if (!changesTakenRef.current) {
      const now = Date.now();
      const prev = readSnapshot(now);
      changesTakenRef.current = {
        changes:
          prev && !isSameLocalDay(prev.at, now)
            ? { since: prev.at, diff: diffSnapshots(prev, providers, erePrice) }
            : null,
        snapshot: snapshotOf(providers, erePrice, now),
      };
    }
    const taken = changesTakenRef.current;
    setVisitChanges(taken.changes);
    setChangesDismissed(isChangesDismissed());
    writeSnapshot(taken.snapshot);
    // Once per mount, on the rows first loaded. MainApp refetches the rows
    // after hydration (App.tsx); a second diff then would be taken against the
    // snapshot this visit has just written.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasProviders]);

  // The strip's lines: the per-offer changes of the offers in the CURRENT
  // view, so a filter change re-reads them; the price and the names always.
  const viewIds = useMemo(() => new Set(filteredProviders.map((p) => p.id)), [filteredProviders]);
  const visitChangeLines = useMemo(
    () => (visitChanges && !changesDismissed ? changeLines(visitChanges.diff, viewIds) : []),
    [visitChanges, changesDismissed, viewIds]
  );
  const changesShown = visitChangeLines.length > 0;
  const changedOffers = changedOfferCount(visitChangeLines);
  // Nothing changed since an earlier day's visit: the trust strip says so.
  const unchangedSince = visitChanges && isEmptyDiff(visitChanges.diff) ? visitChanges.since : null;

  // Reported once, when the strip first appears — a non-interaction in the
  // site's own stream (server/data/siteEvents.ts): it shows itself after mount
  // whatever the visitor does. Never from a state updater.
  const changesViewReported = useRef(false);
  useEffect(() => {
    if (!changesShown || changesViewReported.current) return;
    changesViewReported.current = true;
    trackEvent('compare_changes_view', {
      provider_count: changedOffers,
      surface: SURFACES.comparisonTable,
      language,
    });
  }, [changesShown, changedOffers, language]);

  const dismissChanges = () => {
    trackEvent('compare_changes_dismiss', {
      provider_count: changedOffers,
      surface: SURFACES.comparisonTable,
      language,
    });
    dismissChangesForSession();
    setChangesDismissed(true);
  };

  // ─── "Alle filters" (phones) ───
  // The sheet edits a draft and hands it back on apply; the engine owns the
  // address, so the engine writes it — ONCE, however many filters changed: one
  // `filter_change` per changed filter and one `results_view` for the lot,
  // where the pills would have written (and reported) one by one. Replaced, not
  // pushed: a filter state is not a page the visitor went to.
  /**
   * The two sticky lines — the pill bar below xl, the column-header line from
   * xl; one of them is display: none. The one shown is what a scroll to the
   * results clears.
   */
  const barRef = useRef<HTMLDivElement>(null);
  const headerLineRef = useRef<HTMLDivElement>(null);
  /** Set by an apply that wrote the address; the scroll waits for that render. */
  const scrollAfterApplyRef = useRef(false);
  /**
   * The button that opened the sheet — "Alle filters" on a phone, "Filters"
   * in the bar below lg —, which the focus goes back to when it closes.
   */
  const sheetOpenerRef = useRef<HTMLElement | null>(null);
  const openFilterSheet = (opener: HTMLElement) => {
    sheetOpenerRef.current = opener;
    trackEvent('filter_sheet_open', {
      result_count: filteredProviders.length,
      surface: SURFACES.resultsBar,
      language,
    });
    setSheetOpen(true);
  };

  const cancelFilterSheet = useCallback(
    (resultCount: number) => {
      trackEvent('filter_sheet_cancel', {
        result_count: resultCount,
        surface: SURFACES.resultsBar,
        language,
      });
      setSheetOpen(false);
    },
    [language]
  );

  const applyFilterSheet = (draft: FilterSheetDraft, resultCount: number) => {
    // [URL parameter, its new value (null = remove it), filter_change name, filter_value]
    const writes: [string, string | null, string, string | number][] = [];
    if (draft.audience !== audience) {
      writes.push(['audience', draft.audience, 'audience', draft.audience]);
    }
    // The volume the address will read after this apply, against what it would
    // read with the volume left alone: an untouched field changes nothing (an
    // absent parameter follows the new audience by itself), and a typed value
    // the address would read anyway writes nothing either.
    const volumeAfter = parseVolume(draft.volumeParam, draft.audience);
    if (volumeAfter !== parseVolume(searchParams.get('volume'), draft.audience)) {
      writes.push(['volume', draft.volumeParam, 'volume', volumeAfter]);
    }
    if (draft.contractDuration !== contractDuration) {
      writes.push([
        'contractDuration',
        draft.contractDuration,
        FILTER_EVENT_NAMES.contractDuration,
        draft.contractDuration,
      ]);
    }
    if (draft.smartCharging !== smartChargingFilter) {
      writes.push([
        'smartCharging',
        draft.smartCharging,
        FILTER_EVENT_NAMES.smartCharging,
        draft.smartCharging,
      ]);
    }
    // Against the EFFECTIVE values, which the sheet starts from: a parameter
    // whose control is hidden stays in the address untouched (ignored, not
    // rewritten) unless the visitor picks something else.
    if (draft.terms !== effectiveTerms) {
      writes.push(['terms', draft.terms, FILTER_EVENT_NAMES.terms, draft.terms]);
    }
    if (writes.length > 0) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [key, value] of writes) {
            if (value === null) next.delete(key);
            else next.set(key, value);
          }
          return next;
        },
        { replace: true }
      );
      // The visitor asked to see the new results: bring them in under the
      // bar once the address has been written (the effect below).
      scrollAfterApplyRef.current = true;
    }
    // Reported outside the updater, which React may run twice.
    for (const [, , name, value] of writes) trackFilterChange(name, value);
    trackEvent('filter_sheet_apply', {
      result_count: resultCount,
      surface: SURFACES.resultsBar,
      language,
    });
    setSheetOpen(false);
  };

  // After an apply that changed something, the top of the results — the
  // chips, then the rows — goes just under the sticky bar: the list the
  // visitor asked for ("Toon 3 resultaten") starts on screen, wherever they
  // were when they opened the sheet. Through the one scroll manager, once the
  // new address has rendered (its own key, which a replace gets too): the
  // sticky line's height on top of the header's live offset — the bar's below
  // xl, the column-header line's from xl (a sheet opened on a narrow window
  // can be applied on a wide one); the line not shown measures 0. An apply
  // that changed nothing leaves the page where it is.
  useEffect(() => {
    if (!scrollAfterApplyRef.current) return;
    scrollAfterApplyRef.current = false;
    return scrollToAnchor(RESULTS_ID, {
      extraOffset: Math.max(
        barRef.current?.getBoundingClientRect().height ?? 0,
        headerLineRef.current?.getBoundingClientRect().height ?? 0
      ),
    });
  }, [location.key]);

  // ─── Applied-filter chips (phones) ───
  // One chip per filter away from its default, under the bar; a tap removes
  // exactly that filter's parameter. The focus moves to the chip that takes
  // its place (or to "Alle filters" after the last one, or after "wissen"
  // under them) — never to the page.
  const chipRefs = useRef(new Map<ResettableFilter, HTMLButtonElement>());
  const chipFocusIndexRef = useRef<number | null>(null);
  const removeFilter = (key: ResettableFilter) => {
    chipFocusIndexRef.current = activeFilters.indexOf(key);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete(key);
        return next;
      },
      { replace: true }
    );
    trackFilterChange(FILTER_EVENT_NAMES[key], FILTER_DEFAULTS[key]);
  };
  const activeFiltersKey = activeFilters.join(',');
  useEffect(() => {
    const index = chipFocusIndexRef.current;
    if (index === null) return;
    chipFocusIndexRef.current = null;
    const keys = activeFiltersKey ? (activeFiltersKey.split(',') as ResettableFilter[]) : [];
    const next = keys.length > 0 ? keys[Math.min(index, keys.length - 1)] : null;
    (next ? chipRefs.current.get(next) : sheetTriggerRef.current)?.focus();
  }, [activeFiltersKey]);

  /** What a chip says: the label its option has in the bar. */
  const optionLabel = (options: FilterOption[], value: string) =>
    options.find((o) => o.value === value)?.label ?? value;
  const chipLabel = (key: ResettableFilter): string => {
    switch (key) {
      case 'contractDuration':
        return durationControl.valueText ?? optionLabel(contractOptions, contractDuration);
      case 'smartCharging':
        return optionLabel(smartOptions, smartChargingFilter);
      case 'terms':
        return optionLabel(termsOptions, effectiveTerms);
    }
  };

  // ─── "Wissen" ───
  // Up while a filter it resets is set: the word, where until 2026-10-08 it
  // was a red "✕ n" (the count is the "Filters" and "Alle filters" badges'),
  // in each layout's own place — after the audience in the header line, after
  // the pills in the bar, after the chips on a phone (whose focus then goes to
  // "Alle filters", as after the last chip). Emerald in the two lines, where
  // it clears what is emerald; the chips' grey under them.
  const clearFiltersButton = (place: 'header' | 'bar' | 'chips') =>
    activeFilterCount > 0 ? (
      <button
        type="button"
        onClick={() => {
          if (place === 'chips') chipFocusIndexRef.current = 0;
          clearAllFilters();
        }}
        className={cn(
          'inline-flex shrink-0 items-center gap-1 whitespace-nowrap font-semibold underline-offset-[3px] transition-colors hover:underline focus-ring',
          place === 'chips'
            ? 'min-h-11 rounded-full px-2 text-xs font-bold text-slate-200'
            : 'h-7 text-emerald-300 [html.light_&]:text-[#065f46]',
          // 4 px of padding in the header line: the lead cell is 227 px at a
          // 1280 px window with a classic scrollbar.
          place === 'header' && 'rounded-md px-1 text-xs',
          place === 'bar' && 'rounded-full px-2 text-[11px]'
        )}
      >
        <X className="w-3 h-3 shrink-0" aria-hidden="true" />
        {t('table.clear_filters')}
      </button>
    ) : null;

  // ─── "Toon meer" ───
  const rowRefs = useRef(new Map<string, HTMLLIElement>());
  const showMore = () => {
    const from = visibleCount;
    const batch = filteredProviders.slice(from, from + ITEMS_PER_PAGE);
    trackEvent('results_show_more', {
      page_number: from / ITEMS_PER_PAGE + 1,
      provider_count: filteredProviders.length,
      slugs: batch.map((p) => p.slug).join(','),
      surface: SURFACES.comparisonTable,
      language,
    });
    // Said twice, never three times: the status line below ("20 van 25
    // resultaten getoond") and the row the focus lands on. A separate live
    // region repeating "10 resultaten toegevoegd" made it three, so it is gone
    // (and its key left both baselines with the cleanup of 2026-10-07).
    setVisibleCount((c) => c + ITEMS_PER_PAGE);
    setRevealFocusIndex(from);
    setRevealedBatch({ from, resultSet: resultSetKey });
  };
  // Focus lands on the first row just revealed; the browser scrolls it in.
  useEffect(() => {
    if (revealFocusIndex === null) return;
    const target = visibleProviders[revealFocusIndex];
    if (target) rowRefs.current.get(target.id)?.focus();
    setRevealFocusIndex(null);
  }, [revealFocusIndex, visibleProviders]);

  // ─── Shortlist ───
  const shortlist = useShortlist(providers, language);
  // The tray waits until the results block has come into view: a shortlist
  // remembered from an earlier visit is restored on load, and at the top of the
  // homepage the fixed tray would float over the hero's calls to action on a
  // phone. True once the block's top is at or above the viewport bottom
  // (a quarter of a viewport early, so the tray is up by the time the rows
  // are), or once the page sits below the block altogether; never reset.
  // Client-only: false on both sides of the bake (which renders no tray
  // anyway), and simply true where there is no IntersectionObserver. The flag
  // lives in <AfterInView>, not here (see there).
  const rootRef = useRef<HTMLDivElement>(null);
  const shortlistItems = useMemo(
    () =>
      shortlist.slugs.map((slug) => ({
        slug,
        name: providers.find((p) => p.slug === slug)?.name ?? slug,
      })),
    [shortlist.slugs, providers]
  );
  const shortlistHref = buildShortlistHref(language, shortlist.slugs, {
    audience,
    volume,
    contractDuration,
  });
  const handleTrayCompare = () => {
    trackEvent('compare_tray_click', {
      selected_count: shortlist.slugs.length,
      slugs: shortlist.slugs.join(','),
      surface: SURFACES.comparisonTable,
      language,
    });
  };

  // ─── Per-row handlers ───
  // Every per-provider event keeps `page` = the batch the row sits in, so
  // existing breakdowns keep their meaning now that batches replace pages.
  const batchOf = (index: number) => Math.floor(index / ITEMS_PER_PAGE) + 1;

  const toggleExpanded = (p: EREProvider, index: number) => {
    const id = p.id;
    // Track outside the updater: React may re-invoke updaters (StrictMode
    // double-invokes them in dev), which would double-fire the event.
    if (!expandedIds.has(id)) {
      // providerEventParams so the expand carries the slug join key like every
      // other provider-scoped event (id alone can't join with slug-only ones).
      trackEvent('provider_expand', {
        ...providerEventParams(
          { id, name: p.name, slug: p.slug, position: index + 1, page: batchOf(index) },
          SURFACES.comparisonTable,
          language
        ),
      });
    }
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleSignupClick = (p: EREProvider, index: number) => {
    trackProviderSignupClick(
      {
        id: p.id,
        name: p.name,
        slug: p.slug,
        signupUrl: p.signupUrl,
        position: index + 1,
        page: batchOf(index),
        commissionTier: getCommissionTier(commissionAt(p)),
      },
      SURFACES.comparisonTable,
      language
    );
  };

  // The secondary "Naar website" in the panel foot of an acceptsSignups row
  // (2026-10-23). Its own event, never provider_signup_click: the primary
  // action of such a row is the form, and that event (with its Ads conversion)
  // means the primary CTA sent the visitor away.
  const handleWebsiteClick = (p: EREProvider, index: number) => {
    trackProviderWebsiteClick(
      {
        id: p.id,
        name: p.name,
        slug: p.slug,
        position: index + 1,
        page: batchOf(index),
        commissionTier: getCommissionTier(commissionAt(p)),
      },
      SURFACES.comparisonTable,
      language,
      'panel'
    );
  };

  // In-site signup form (acceptsSignups rows): opens the modal instead of
  // leaving for the provider's site. Deliberately does NOT fire
  // trackProviderSignupClick — that event means "left for the provider".
  const openSignupModal = (p: EREProvider, index: number) => {
    const tier = getCommissionTier(commissionAt(p));
    trackEvent(
      'signup_form_open',
      providerEventParams(
        {
          id: p.id,
          name: p.name,
          slug: p.slug,
          position: index + 1,
          page: batchOf(index),
          commissionTier: tier,
        },
        SURFACES.comparisonTable,
        language
      )
    );
    // Flat row ids are `${providerId}-${contractId}` — the modal needs the
    // provider id the server-side re-check resolves against; the remainder is
    // the contract row this CTA belongs to (the selected aanbod).
    const [rowProviderId, ...contractIdParts] = String(p.id).split('-');
    setSignupCtx({
      providerId: rowProviderId,
      providerName: p.name,
      providerSlug: p.slug,
      contractId: contractIdParts.length > 0 ? contractIdParts.join('-') : null,
      targetAudience: p.targetAudience ?? null,
      contractDuration: p.contractDuration,
      // The provider's own gegevensvereisten, sanitised here rather than in the
      // wizard: this payload can be a page baked before an item kind existed,
      // and the form must degrade to "ask nothing extra" instead of rendering a
      // question it does not understand. Absent = [].
      items: sanitizeLeadPublicItems(p.signupItems),
      // The volume the visitor set above the results, prefilled into the
      // wizard's laadvolume field; null while the comparer is on its default.
      annualKwh: explicitComparerVolume(searchParams),
      surface: SURFACES.comparisonTable,
      position: index + 1,
      commissionTier: tier,
    });
  };

  const handleProfileClick = (p: EREProvider, index: number) => {
    trackViewProfileClick(
      {
        id: p.id,
        name: p.name,
        slug: p.slug,
        position: index + 1,
        page: batchOf(index),
      },
      SURFACES.comparisonTable,
      language
    );
  };

  // The CTA target is resolved server-side (contract signup link → custom
  // aanmeldpagina → homepage); the homepage is only a belt-and-braces fallback
  // for a row that somehow arrives without one.
  const resolveSignupUrl = (p: EREProvider): string => p.signupUrl || p.providerUrl || '';

  const getTrackingUrl = (url: string, audienceOfRow: EREProvider['targetAudience']) =>
    buildOutboundUrl(url, {
      medium: 'standaard_resultaat',
      content: audienceOfRow === 'Zakelijk' ? 'zakelijk' : 'particulier',
    });

  return (
    // Every motion inside — the details panels, the filter menus, the tray's
    // enter and exit — follows the visitor's reduced-motion setting.
    <MotionConfig reducedMotion="user">
      <div ref={rootRef} id="compare" data-comparer="rows" className="relative">
        <span id="vergelijken" aria-hidden="true" className="block absolute top-0" />
        {/* Fold accent — thin centred line. In light mode, soft emerald bloom
          flanks (absolute, no layout impact) diffuse the otherwise-harsh edge
          against the cream background; dark mode keeps the original look. */}
        <div className="relative pointer-events-none">
          <div className="dark:hidden absolute inset-x-0 -top-5 h-5 bg-gradient-to-b from-transparent to-emerald-500/[0.07]" />
          <div className="h-px bg-gradient-to-r from-transparent via-emerald-500/30 to-transparent" />
          <div className="dark:hidden absolute inset-x-0 top-full h-5 bg-gradient-to-t from-transparent to-emerald-500/[0.07]" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-3 sm:pt-6 pb-12">
          {/* ── Stap 2 header: kicker, result count, the trust strip, the basis line ──
            Four paragraphs used to sit here (the ranking disclosure, the
            indicative-estimate note, "berekend op basis van … ≈ … ERE's" and the
            btw note). The strip says what they said in four short items, each
            with its proof one tap away; the basis line says what every figure
            below is calculated with.
            Below sm the header is count → ranking line → bar (2026-10-14): no
            kicker — a phone arrives here from a "Vergelijken" tap, not from a
            Stap 1 beside it — the strip folded to its first item and half the
            top padding. Measured at 375 × 812 on the live data: the first
            result card started 432 px under the spot a CTA lands on, and ended
            at 813 px; it starts 274 px under it now and is whole on a
            664 px iPhone screen. */}
          <div className="mb-6">
            <div className="hidden sm:flex items-center gap-3 mb-4">
              <div className="bg-emerald-900/40 p-2.5 rounded-xl shadow-sm shrink-0 border border-emerald-800/30">
                <GitCompareArrows className="w-5 h-5 text-emerald-500" />
              </div>
              <span className="text-[11px] font-black uppercase tracking-[0.2em] text-emerald-400 [html.light_&]:text-emerald-700">
                {t('calculator.compare_title')}
              </span>
            </div>
            <h2 className="text-2xl md:text-3xl font-extrabold text-white mb-3">
              {formatInteger(filteredProviders.length, language)}{' '}
              {filteredProviders.length === 1 ? t('table.result') : t('table.results')}
            </h2>

            {/* The ranking disclosure is the strip's first item: static markup,
              in the baked HTML for every reader, JS or not. */}
            <TrustStrip
              offerCount={coverage.offers}
              providerCount={coverage.providers}
              updatedAt={providersUpdatedAt}
              lastVisitAt={unchangedSince}
              phoneMoreOpen={trustOpen}
              onPhoneMoreToggle={toggleTrustOpen}
            />

            {/* What changed since the visitor's previous visit — client-only,
              filled by the effect above, never in the bake. */}
            {visitChanges && changesShown && (
              <ChangesStrip
                since={visitChanges.since}
                lines={visitChangeLines}
                onDismiss={dismissChanges}
              />
            )}

            {/* The basis line, and the estimator right after it — on one line
              where both fit (from lg), the line first and the link under it
              where they do not: the assumption, then the way to correct it. */}
            <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2">
              {/* The volume, its EREs and which btw regime the rates carry,
                ending in "Geen gegarandeerd bedrag" — the note the asterisk on
                "Netto per jaar*" in the header line (xl) points at, hence the
                asterisk in front of it. */}
              {volume > 0 && (
                <p id={BASIS_LINE_ID} className="text-xs text-fg-muted text-pretty">
                  <span aria-hidden="true">* </span>
                  {fillTemplate(t('comparison.basis_line'), {
                    volume: formatInteger(volume, language),
                    ere: formatInteger(ereCountFor(volume), language),
                    btw: t(
                      audience === 'Zakelijk'
                        ? 'comparison.btw_short_business'
                        : audience === 'Particulier'
                          ? 'comparison.btw_short_particulier'
                          : 'comparison.btw_short_mixed'
                    ),
                  })}
                </p>
              )}

              {/* Second door to the kWh estimator, at the point where the number
                matters: a visitor who reaches the results on the default volume
                still has no idea whether 2 500 kWh is "them". Opens the modal
                that lives in the filters card, via the URL param it consumes. */}
              <button
                type="button"
                onClick={() =>
                  setSearchParams(
                    (prev) => {
                      const next = new URLSearchParams(prev);
                      next.set('estimator', 'results');
                      return next;
                    },
                    { replace: true }
                  )
                }
                className="inline-flex items-center gap-1.5 text-xs font-bold text-emerald-400 [html.light_&]:text-emerald-700 hover:text-emerald-300 underline underline-offset-2"
              >
                <Calculator className="w-3.5 h-3.5" aria-hidden="true" />
                {t('comparison.estimator_cta')}
              </button>
            </div>

            {/* The yield assumption, said where a business reader decides:
              the site converts at one factor (standard grid mix) for every
              row. Deliberately no GvO multiplier or toggle — the doubling
              the FAQ mentions has no primary source in docs/nea/ and no
              offering records GvO support, so the comparer neither states
              nor computes it. */}
            {volume > 0 && audience === 'Zakelijk' && (
              <div className="mt-2 flex items-center gap-2 text-[10px] font-black uppercase tracking-wider text-fg-muted">
                <div className="w-1 h-1 rounded-full bg-emerald-500" />
                <span>
                  {fillTemplate(t('table.yield_note_business'), {
                    factor: formatDecimal(KWH_TO_ERE_FACTOR, language, 4),
                  })}
                </span>
              </div>
            )}
          </div>

          {/* ── Filter + sort bar (below xl) ──
            It sticks at the header's live bottom edge, --header-offset (see
            index.css): in its own slot under the Stap 2 block until the slot
            reaches the header, then just under it — and against the viewport
            top once the header has slid away. `data-follows-header` lets the
            header glide it along when it flips (src/lib/headerFollow.ts); a
            bar resting in its slot never moves. The previous version sat at
            the header's full height and TRANSLATED by what the header had
            given up, which moved it whether it was stuck or not: with the
            header away it covered the bottom 56 px of the Stap 2 block and
            jumped 80 px at every flip. The frosted glass is desktop-only (lg),
            like the header's.
            Two layouts in it since 2026-10-08 (the filter-bar mockup: below
            sm composition B, from sm the grouped bar A), each one row:
            - below sm, two buttons that share the row — "Alle filters" (n),
              the filter sheet, and "Sorteren: <order>", the sort sheet. A
              one-row pill bar showed 153 of its 429 px at 375 px, so three of
              four filters (and a set one) sat behind its fade; two labelled
              buttons hide nothing and make filtering and sorting two acts.
              What is set shows as chips under the bar.
            - from sm to xl, the filters under a muted "Filters" label with
              their count, each pill naming its filter and value ("Looptijd:
              2 jaar"; "alle" where it filters nothing), "wissen" after them,
              and at the right end, after a 1 px divider with 16 px either
              side, the sort — boxed, labelled "Sorteren:", never one more
              pill. The pills scroll sideways where the row is too narrow (a
              fade at the right edge below lg); the sort stays pinned.
            Not from xl: there the column-header line below is the one line of
            controls — its column words are these filters, the audience leads
            it with "wissen", the sort ends it (2026-10-03; until then this
            bar stood above the list from xl as a loose row of pills). On the
            clean address no count and no "wissen" are printed — the bake and
            the first client render agree; a filtered address is served as the
            SPA shell, so nothing baked ever carries a count. */}
          <div
            ref={barRef}
            data-results-bar
            data-follows-header
            className="sticky top-[var(--header-offset)] z-30 -mx-4 sm:-mx-6 lg:-mx-8 mb-4 xl:hidden"
          >
            <div className="bg-slate-900 lg:bg-slate-900/85 lg:backdrop-blur-2xl border-y border-slate-700/30 shadow-sm">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Below sm: two 36 px buttons that fill the row, each as wide
                  as its words plus half the room left (at 375 px "Alle
                  filters" + a count and "Sorteren: Contractduur ↑" both fit),
                  each a tap target of the bar's whole height (the transparent
                  ::before). Light: a white button with a slate-400 border,
                  literal hexes (the slate tokens make a pale field on the
                  pale bar, and --color-white is ink there). Set — a filter in
                  force, a sort away from the default — an emerald border. */}
                <div data-phone-bar className="flex items-center gap-2 py-1.5 sm:hidden">
                  <button
                    ref={sheetTriggerRef}
                    type="button"
                    aria-haspopup="dialog"
                    aria-label={filterSheetName}
                    onClick={(e) => openFilterSheet(e.currentTarget)}
                    className={cn(PHONE_BAR_BUTTON_CLASS, phoneSetClass(activeFilterCount > 0))}
                  >
                    <SlidersHorizontal className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
                    <span className="truncate">{t('filters.sheet_open')}</span>
                    {activeFilterCount > 0 && (
                      <span aria-hidden="true" className={cn(COUNT_BADGE_CLASS, 'ml-0.5')}>
                        {formatInteger(activeFilterCount, language)}
                      </span>
                    )}
                  </button>
                  <button
                    ref={sortSheetTriggerRef}
                    type="button"
                    aria-haspopup="dialog"
                    aria-label={`${sortName}: ${sortSelected?.label ?? sortKey}`}
                    onClick={() => setSortSheetOpen(true)}
                    className={cn(
                      PHONE_BAR_BUTTON_CLASS,
                      phoneSetClass(sortKey !== DEFAULT_SORT_KEY)
                    )}
                  >
                    <ArrowUpDown className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
                    <span className="shrink-0 font-medium text-fg-muted">{sortLabel}</span>
                    <span className="min-w-0 truncate">
                      {sortSelected?.shortLabel ?? sortSelected?.label ?? sortKey}
                    </span>
                    <ChevronDown className="w-3 h-3 shrink-0 opacity-60" aria-hidden="true" />
                  </button>
                </div>

                {/* From sm to xl: the grouped bar. */}
                <div className="hidden sm:flex items-center gap-2 py-2">
                  <div
                    role="group"
                    aria-label={filtersName}
                    className="flex min-w-0 flex-1 items-center gap-2"
                  >
                    {/* "Filters" with the count. Below lg it is also the button
                      to the filter sheet: there the named pills outgrow the
                      row (at 768 px 574 px of pills for a 433 px scroller,
                      measured 2026-10-08), and the sheet holds every filter —
                      what the sliders button was below lg until then. Named
                      by what it shows, "Filters (n)". From lg the row fits
                      and the label is a label, its name the group's. */}
                    <button
                      type="button"
                      aria-haspopup="dialog"
                      aria-label={filtersName}
                      onClick={(e) => openFilterSheet(e.currentTarget)}
                      className="-ml-2 inline-flex h-7 shrink-0 items-center gap-1.5 rounded-full px-2 text-[10px] font-black uppercase tracking-wider text-fg-muted transition-colors hover:bg-slate-800/70 hover:text-slate-200 focus-ring lg:hidden"
                    >
                      {filtersLabelContent}
                    </button>
                    <span
                      aria-hidden="true"
                      className="hidden shrink-0 items-center gap-1.5 text-[10px] font-black uppercase tracking-wider text-fg-muted lg:inline-flex"
                    >
                      {filtersLabelContent}
                    </span>
                    {/* The pills — the right-edge fade (below lg) hints that more
                      sit off-screen; the scrollbar itself is hidden. 4 px of
                      room around them inside the scroller, so a focus ring is
                      not cut off by it, and 32 px at its end below lg, so the
                      last pill can scroll out from under the fade. As wide as
                      the pills, so "wissen" follows the last one. */}
                    <div className="relative min-w-0 after:pointer-events-none after:absolute after:inset-y-0 after:right-0 after:w-8 after:bg-gradient-to-l after:from-slate-900 after:to-transparent lg:after:hidden">
                      <div className="-my-1 -ml-1 flex items-center gap-1.5 overflow-x-auto scrollbar-none py-1 pl-1 pr-8 lg:pr-1">
                        <FilterSelect
                          {...audienceControl}
                          name={t('table.audience_short')}
                          label={pillLabel(t('table.audience_short'))}
                          markSet={false}
                        />
                        <FilterSelect
                          {...durationControl}
                          name={t('table.duration')}
                          label={pillLabel(t('table.duration'))}
                          valueText={
                            durationControl.value === ALL_DURATIONS
                              ? allWord
                              : durationControl.valueText
                          }
                        />
                        {termsControl && (
                          <FilterSelect
                            {...termsControl}
                            name={t('table.col_terms')}
                            label={pillLabel(t('table.col_terms'))}
                            valueText={termsControl.value === 'all' ? allWord : undefined}
                          />
                        )}
                        <FilterSelect
                          {...smartControl}
                          name={t('table.col_smart_charger')}
                          label={pillLabel(t('table.col_smart_charger'))}
                          valueText={smartControl.value === 'all' ? allWord : undefined}
                        />
                      </div>
                    </div>
                    {/* Pinned after the scroller: never behind its fade. */}
                    {clearFiltersButton('bar')}
                  </div>
                  {/* The sort, pinned: a 1 px divider with 16 px either side. */}
                  <span aria-hidden="true" className="ml-2 h-4 w-px shrink-0 bg-slate-700/60" />
                  <div className="ml-2 flex shrink-0 items-center">{barSortControl}</div>
                </div>
              </div>
            </div>
          </div>

          {/* ── The column-header line (xl+) ──
            The one line of controls from xl, and the one that sticks: the
            column words over the rows' own grid template — Looptijd,
            Voorwaarden and Slimme lader open their filter's menu —, the
            audience and "wissen" at its start,
            and at its end, over the two action columns, the sort
            (ResultHeaderRow). The same control descriptions and the same sort
            state as the bar's, which is display: none from xl: two views of
            one address. The bar's slot rules: `top-[var(--header-offset)]` +
            `data-follows-header` (the header glides it along when it flips),
            and the band and frosted glass the bar has from lg. Over the empty
            state too: the filters that emptied the list live here. The band
            spans the window, as the site header above it does (2026-10-08,
            the filter-bar mockup: at 1440 px it was a 1280 px box between
            two 80 px gutters): negative margins of half of what the window
            has over 80rem, and the same margins given back to the box inside,
            which therefore is the rows' own box at every width — also with a
            classic scrollbar, which 100vw counts (the band then reaches
            7.5 px past each edge; the page clips it, body overflow-x). */}
          <div
            ref={headerLineRef}
            data-follows-header
            className="hidden xl:block sticky top-[var(--header-offset)] z-30 -mx-8 mb-4"
          >
            <div className="mx-[calc((80rem_-_100vw)/2)] bg-slate-900/85 backdrop-blur-2xl border-y border-slate-700/30 shadow-sm">
              <div className="mx-[calc((100vw_-_80rem)/2)] px-8">
                <ResultHeaderRow
                  audience={audienceControl}
                  duration={durationControl}
                  smartCharging={smartControl}
                  terms={termsControl}
                  onSmartChargerInfo={() => setSmartChargingInfoOpen(true)}
                  clearControl={clearFiltersButton('header')}
                  sortControl={headerSortControl}
                />
              </div>
            </div>
          </div>

          {/* ── The results: chips, rows, "Toon meer" ── one block, whose top a
            sheet apply scrolls under the bar. No box of its own: the chips'
            negative top margin collapses through it as before. */}
          <div id={RESULTS_ID}>
            {/* ── Applied filters (below sm) ──
            What narrows the list, under the bar, while any filter is away from
            its default: the visitor's segment first — shown, never removable:
            it is who they are, not a filter they set — then one chip per
            filter, and a tap removes it, then "wissen". Not sticky: the bar is
            the part that stays. Below sm only since 2026-10-08 (lg until
            then): from sm the bar's pills say their values themselves. A
            function of the address on both sides of the bake, and the clean
            address has none, so the HTML never differs from the first client
            render. */}
            {activeFilterCount > 0 && (
              <ul
                aria-label={t('table.active_filters')}
                className="-mt-1 mb-4 flex flex-wrap items-center gap-2 sm:hidden"
              >
                <li className="inline-flex min-h-11 items-center gap-1.5 rounded-full border border-slate-700/70 bg-slate-800/60 px-3.5 text-xs font-semibold text-slate-300">
                  <Users2 className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
                  <span className="sr-only">{t('table.audience')}: </span>
                  {optionLabel(audienceOptions, audience)}
                </li>
                {activeFilters.map((key) => {
                  const label = chipLabel(key);
                  return (
                    <li key={key}>
                      <button
                        ref={(el) => {
                          if (el) chipRefs.current.set(key, el);
                          else chipRefs.current.delete(key);
                        }}
                        type="button"
                        onClick={() => removeFilter(key)}
                        aria-label={fillTemplate(t('filters.chip_remove_aria'), { label })}
                        className="inline-flex min-h-11 items-center gap-1.5 rounded-full border border-emerald-500/40 bg-emerald-500/10 pl-3.5 pr-3 text-xs font-semibold text-emerald-300 transition-colors hover:bg-emerald-500/20 focus-ring [html.light_&]:text-[#065f46]"
                      >
                        {label}
                        <X className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
                      </button>
                    </li>
                  );
                })}
                {/* The bar has two buttons only, so "wissen" ends the chips. */}
                <li>{clearFiltersButton('chips')}</li>
              </ul>
            )}

            {/* ── The rows ── */}
            {filteredProviders.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 text-center">
                <div className="w-16 h-16 rounded-2xl bg-slate-800 border border-slate-700 flex items-center justify-center mb-5">
                  <SlidersHorizontal className="w-7 h-7 text-fg-muted" />
                </div>
                <h2 className="text-lg font-bold text-white mb-2">{t('comparison.no_results')}</h2>
                <p className="text-sm text-slate-400 mb-6 max-w-md">
                  {t('comparison.clear_filters_to_see')}
                </p>
                <button
                  onClick={clearAllFilters}
                  className="px-5 py-2.5 bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-bold rounded-xl transition-colors shadow-lg shadow-emerald-900/20"
                >
                  {t('table.clear_filters')}
                </button>
              </div>
            ) : (
              <>
                <ul
                  aria-label={t('calculator.compare_title')}
                  className={cn(
                    // 12 px at every width: the tags on a row's top edge stand 7 px above it.
                    'space-y-3',
                    // Room for the tray, so the last row and "Toon meer" stay reachable.
                    shortlist.slugs.length > 0 && 'pb-24 lg:pb-28'
                  )}
                >
                  {visibleProviders.map((p, index) => {
                    const earnings = calculateEarnings(p);
                    const hasUnknownData = p.commissionFee === null || p.contractDuration === null;
                    const standing = standingOf(p);
                    return (
                      <ResultRow
                        key={p.id}
                        provider={p}
                        index={index}
                        earnings={earnings}
                        volume={volume}
                        erePrice={erePrice}
                        isTopEarner={standing.topEarnings}
                        isLowestCommission={standing.lowestCommission}
                        relative={relativePluspunten(standing, sortField, sortDirection)}
                        showAudiencePill={audience === 'Alle'}
                        entranceStep={entranceStepOf(index)}
                        expanded={expandedIds.has(p.id)}
                        mountDetails={mountDetails}
                        detailsEpoch={detailsEpoch}
                        selected={shortlist.isSelected(p.slug)}
                        selectable={shortlist.canAdd}
                        ctaHref={
                          hasUnknownData
                            ? null
                            : getTrackingUrl(resolveSignupUrl(p), p.targetAudience)
                        }
                        // The panel foot's secondary link of a form-on row goes to the
                        // provider's HOMEPAGE (Jasper 2026-10-23), never to the signup
                        // chain the CTA resolves — that is what the form replaces.
                        websiteHref={
                          hasUnknownData || !p.acceptsSignups || !p.providerUrl
                            ? null
                            : getTrackingUrl(p.providerUrl, p.targetAudience)
                        }
                        onToggleExpanded={() => toggleExpanded(p, index)}
                        onToggleSelected={() => shortlist.toggle(p, index + 1)}
                        onSignupClick={() => handleSignupClick(p, index)}
                        onOpenSignup={() => openSignupModal(p, index)}
                        onWebsiteClick={() => handleWebsiteClick(p, index)}
                        onProfileClick={() => handleProfileClick(p, index)}
                        onSmartChargerInfo={() => setSmartChargingInfoOpen(true)}
                        rowRef={(el) => {
                          if (el) rowRefs.current.set(p.id, el);
                          else rowRefs.current.delete(p.id);
                        }}
                      />
                    );
                  })}
                </ul>

                {/* ── "Toon meer" — static markup, identical on both sides of the
                bake; without JS it does nothing, like the pager it replaced. */}
                {filteredProviders.length > ITEMS_PER_PAGE && (
                  <div className="mt-6 flex flex-col items-center gap-2">
                    {remaining > 0 && (
                      <button
                        type="button"
                        onClick={showMore}
                        className="h-11 px-5 rounded-xl bg-slate-800 border border-slate-700/60 hover:border-emerald-500/50 hover:bg-slate-700/60 text-sm font-bold text-white transition-colors focus-ring"
                      >
                        {fillTemplate(t('table.show_more'), {
                          count: formatInteger(Math.min(ITEMS_PER_PAGE, remaining), language),
                        })}
                      </button>
                    )}
                    <p role="status" className="text-xs text-fg-muted tabular-nums">
                      {fillTemplate(t('table.shown_of_total'), {
                        shown: formatInteger(visibleProviders.length, language),
                        total: formatInteger(filteredProviders.length, language),
                      })}
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* A floating overlay that throws must never take the results down. */}
        <AfterInView target={rootRef}>
          <ErrorBoundary fallback={null} label="shortlist-tray">
            <ShortlistTray
              items={shortlistItems}
              href={shortlistHref}
              backState={providerBackState(location)}
              onRemove={shortlist.remove}
              onClear={shortlist.clear}
              onCompareClick={handleTrayCompare}
            />
          </ErrorBoundary>
        </AfterInView>

        <LazyOnOpen open={!!signupCtx}>
          <SignupModal open={!!signupCtx} context={signupCtx} onClose={() => setSignupCtx(null)} />
        </LazyOnOpen>
        {/* Starts from the address each time it opens — the EFFECTIVE values,
          so a filter whose control is hidden is neither shown nor rewritten. */}
        <FilterSheet
          open={sheetOpen}
          initial={{
            audience,
            volumeParam: searchParams.get('volume'),
            contractDuration,
            smartCharging: smartChargingFilter,
            terms: effectiveTerms,
          }}
          providers={providers}
          gates={filterGates}
          onApply={applyFilterSheet}
          onCancel={cancelFilterSheet}
          returnFocusRef={sheetOpenerRef}
        />
        {/* The orders of the sort control as a sheet (phones): a tap sorts
          through the one handler — the same `sort_change`, nothing for the
          order already in force — and closes it. */}
        <SortSheet
          open={sortSheetOpen}
          options={sortOptions}
          value={sortKey}
          onSelect={(key) => {
            handleSortSelect(key);
            setSortSheetOpen(false);
          }}
          onClose={() => setSortSheetOpen(false)}
          returnFocusRef={sortSheetTriggerRef}
        />
        <SmartChargerInfoModal
          open={smartChargingInfoOpen}
          onClose={() => setSmartChargingInfoOpen(false)}
          onOpenMidChecker={() => {
            setSmartChargingInfoOpen(false);
            setMidCheckerOpen(true);
            trackEvent('mid_checker_open', { surface: SURFACES.smartChargerModal, language });
          }}
        />
        <LazyOnOpen open={midCheckerOpen}>
          <MidCheckerModal
            open={midCheckerOpen}
            onClose={() => setMidCheckerOpen(false)}
            onPathSelect={(path) => {
              if (path === 'has') setSmartChargingFilter('has');
              else if (path === 'none') setSmartChargingFilter('none');
              // 'unknown': leave the visitor's existing filter alone.
            }}
          />
        </LazyOnOpen>
      </div>
    </MotionConfig>
  );
};
